import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';
import { applyModuleSeo } from '../../utils/seo';

const CANONICAL = 'https://explanatum.co.uk/modules/affiliate-marketing';

const AffiliateMarketingLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Affiliate Marketing Engine | Explanatum';
    const desc = 'Launch, scale, and automate your affiliate program with onboarding flows, tiered commissions, payout automation, and CRM-ready attribution in one workspace.';
    applyModuleSeo({
      title,
      description: desc,
      canonicalUrl: CANONICAL,
      keywords: [
        'affiliate marketing software',
        'ai affiliate program',
        'partner portal automation',
        'commission tracking platform',
        'explanatum affiliate engine'
      ],
      structuredDataId: 'ld-module-affiliate-marketing',
      softwareName: 'AI Affiliate Marketing Engine',
      softwareCategory: 'MarketingApplication'
    });
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Affiliate marketing engine overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-pink-400/40 bg-pink-500/10 px-4 py-1 text-sm font-semibold text-pink-200">
                <span className="inline-flex h-2 w-2 rounded-full bg-pink-300" />
                AI Affiliate Marketing Engine
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Recruit partners, track revenue, automate payouts.
                <span className="mt-2 block text-blue-600">
                  Your entire affiliate channel, orchestrated by Explanatum.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Give partners a branded portal, launch tiered commissions, automate webhooks, and review real-time deal attribution. Explanatum replaces spreadsheets, legacy affiliate networks like AWIN, and agency retainers with a measurable growth channel.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_affiliate_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 via-indigo-500 to-sky-400 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-pink-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/marketing/affiliate-management"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open affiliate workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Guided program setup & approvals queue
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  Tiered commissions with automated payouts
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Attribution, webhooks, and CRM sync
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Affiliate dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-pink-500/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Program health</span>
                  <span>Real-time metrics</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">30-day revenue £42,800 • Conversion rate 5.2% • Top tier: Gold (18 partners). Automated Slack alerts on partner inactivity.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Payout queue ready: £6,120 due Friday. Webhooks firing to Stripe, HubSpot, and your BI warehouse.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Latest signup: Growth Labs • auto-approved to Tier 1. Partner assets delivered via branded portal.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Affiliate growth without juggling tools or agencies</h2>
            <p className="mt-4 text-base text-gray-500">Launch fast, keep incentives transparent, and see partner impact alongside the rest of your go-to-market data.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Program setup wizard',
              body: 'Define commission models, cookie windows, and onboarding flows once. Explanatum generates approvals, contracts, and launch checklists.'
            }, {
              title: 'Tiered incentives & bonuses',
              body: 'Create multi-level plans, one-off bonuses, and milestone rewards with guardrails so finance teams stay aligned.'
            }, {
              title: 'Partner command center',
              body: 'Affiliates receive dashboards, creatives, tracking links, and earnings in real time—no shared drives or manual emails.'
            }, {
              title: 'Attribution & compliance',
              body: 'Track clicks, conversions, and LTV from each partner. Integrate webhooks and suppression lists to stay compliant globally.'
            }, {
              title: 'Payout automation',
              body: 'Bundle commissions, trigger payouts, and sync ledger entries to your finance stack with auditable history.'
            }, {
              title: 'Analytics & forecasting',
              body: 'Surface partner cohorts, forecast pipeline contribution, and sync insights to CRM dashboards and the AI Business Consultant.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams upgrade from AWIN and full-service agencies</h2>
            <p className="mt-4 text-base text-gray-500">Affiliate partners compound reach with owned relationships and transparent attribution—without handing control to third-party networks or high-retainer agencies.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Affiliate Marketing Engine',
              points: [
                'Branded partner portal with instant onboarding',
                'Tiered commissions, bonuses, and automated payouts',
                'Conversion tracking, attribution, and CRM sync in real time',
                'Creative library, tracking links, and compliance guardrails',
                'Integrated with AI consultant for cross-channel follow-up'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'AWIN & legacy affiliate networks',
              points: [
                'Network-first view limits visibility into partner relationships',
                'Approval workflows and creatives controlled outside your brand',
                'Reporting silos require exporting data into spreadsheets and BI tools',
                'Limited flexibility for custom tiers, bonuses, or product bundles',
                'Separate contracts, billing, and fees for each market you join'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Full-service affiliate agencies',
              points: [
                '£3,000–£8,000 retainers plus margin on payouts',
                'Limited transparency into strategy, targeting, or partner selection',
                'Scale depends on agency capacity, not your partners’ enthusiasm',
                'Attribution and CRM updates remain manual and delayed',
                'Relationship equity disappears when the contract ends'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-gray-200 bg-sky-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need 50 partners promoting you by next quarter?”</h3>
            <p className="mt-3 text-base text-gray-600">Launch recruitment funnels, auto-approve qualified partners, and keep every conversion tracked without hiring agencies.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_affiliate_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 via-indigo-500 to-blue-600 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-pink-400/30 hover:scale-[1.02]"
              >
                Launch partners now
              </a>
              <a
                href="/modules/cold-email"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Compare outbound options
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where the affiliate engine pays off quickly</h2>
            <p className="mt-4 text-base text-gray-500">From SaaS revenue to creator partnerships, Explanatum keeps every partner accountable and equipped.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'SaaS & digital products',
              body: 'Offer tiered commissions and MRR-based bonuses while logging upgrades, downgrades, and churn right back into CRM.'
            }, {
              title: 'Education & community brands',
              body: 'Give influencers custom funnels, track course seats sold, and segment payouts by cohort performance.'
            }, {
              title: 'Marketplaces & services',
              body: 'Equip agencies and integrators with creative kits, watch pipeline sourced, and feed hot leads into the Business Consultant for follow-up.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default AffiliateMarketingLanding;
